<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Session &amp; Semester Settings</h1>
        <button class="btn btn-primary" @click="openCreateModal">
          <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Session
        </button>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div v-if="loading" class="text-center my-4">
            <b-spinner variant="primary"></b-spinner>
            <div class="mt-2 text-muted">Loading settings...</div>
          </div>

          <template v-else>
            <div class="row mb-3">
              <div class="col-md-4">
                <b-form-input
                  v-model="filter"
                  placeholder="Search sessions..."
                  debounce="300"
                ></b-form-input>
              </div>
            </div>

            <b-table
              :items="settings"
              :fields="fields"
              :filter="filter"
              :per-page="perPage"
              :current-page="currentPage"
              responsive
              striped
              hover
              show-empty
            >
              <template #empty>
                <div class="text-center text-muted py-4">No settings found.</div>
              </template>
              <template #cell(index)="data">
                {{ (currentPage - 1) * perPage + data.index + 1 }}
              </template>
              <template #cell(status)="data">
                <b-badge :variant="data.value === 'active' ? 'success' : 'secondary'">
                  {{ data.value === 'active' ? 'Active' : 'Inactive' }}
                </b-badge>
              </template>
              <template #cell(created_at)="data">
                {{ formatDate(data.value) }}
              </template>
              <template #cell(actions)="data">
                <button
                  v-if="data.item.status !== 'active'"
                  class="btn btn-sm btn-success mr-1"
                  @click="setActive(data.item)"
                  :disabled="activating"
                  title="Set as Active"
                >
                  <b-spinner small v-if="activatingId === data.item.id"></b-spinner>
                  <b-icon icon="check-circle" v-else></b-icon>
                </button>
                <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" title="Edit">
                  <b-icon icon="pencil"></b-icon>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="confirmDelete(data.item)"
                  :disabled="data.item.status === 'active'"
                  title="Delete"
                >
                  <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                  <b-icon icon="trash" v-else></b-icon>
                </button>
              </template>
            </b-table>

            <div class="d-flex justify-content-between align-items-center" v-if="settings.length > perPage">
              <small class="text-muted">{{ settings.length }} sessions total</small>
              <b-pagination
                v-model="currentPage"
                :total-rows="settings.length"
                :per-page="perPage"
                size="sm"
              ></b-pagination>
            </div>

            <div class="mt-4 p-3 bg-light rounded" v-if="activeSetting">
              <h5 class="mb-2">Current Active Session</h5>
              <div class="row">
                <div class="col-md-4">
                  <strong>Session:</strong> {{ activeSetting.session_name }}
                </div>
                <div class="col-md-4">
                  <strong>Semester:</strong> {{ activeSetting.semester_name }}
                </div>
                <div class="col-md-4">
                  <strong>Status:</strong>
                  <b-badge variant="success">Active</b-badge>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Create / Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit Session Setting' : 'Add Session Setting'"
        @ok.prevent="saveSetting"
        :ok-disabled="saving"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : (isEditing ? 'Update' : 'Create') }}
        </template>
        <b-form @submit.prevent="saveSetting">
          <b-form-group label="Session Name" label-for="session-name">
            <b-form-input
              id="session-name"
              v-model="form.session_name"
              placeholder="e.g. 2025/2026"
              :disabled="saving"
              :state="formErrors.session_name ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.session_name }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group label="Semester" label-for="semester-name">
            <b-form-select id="semester-name" v-model="form.semester_name" :disabled="saving" :state="formErrors.semester_name ? false : null">
              <option :value="null">-- Select Semester --</option>
              <option value="FIRST SEMESTER">FIRST SEMESTER</option>
              <option value="SECOND SEMESTER">SECOND SEMESTER</option>
              <option value="THIRD SEMESTER">THIRD SEMESTER</option>
              <option value="FOURTH SEMESTER">FOURTH SEMESTER</option>
            </b-form-select>
            <b-form-invalid-feedback>{{ formErrors.semester_name }}</b-form-invalid-feedback>
          </b-form-group>
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Session ID" label-for="session-id">
                <b-form-input
                  id="session-id"
                  v-model.number="form.session_id"
                  type="number"
                  placeholder="Optional"
                  :disabled="saving"
                ></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Semester ID" label-for="semester-id">
                <b-form-input
                  id="semester-id"
                  v-model.number="form.semester_id"
                  type="number"
                  placeholder="Optional"
                  :disabled="saving"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>
        </b-form>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      settings: [],
      loading: false,
      saving: false,
      activating: false,
      activatingId: null,
      deletingId: null,
      filter: "",
      perPage: 15,
      currentPage: 1,
      showModal: false,
      isEditing: false,
      editId: null,
      form: {
        session_name: "",
        semester_name: null,
        session_id: null,
        semester_id: null,
      },
      formErrors: {},
      fields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "50px" } },
        { key: "session_name", label: "Session", sortable: true },
        { key: "semester_name", label: "Semester", sortable: true },
        { key: "status", label: "Status", class: "text-center", sortable: true },
        { key: "created_at", label: "Created", sortable: true },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "150px" } },
      ],
    };
  },
  computed: {
    activeSetting() {
      return this.settings.find((s) => s.status === "active") || null;
    },
  },
  created() {
    this.loadSettings();
  },
  methods: {
    loadSettings() {
      this.loading = true;
      axios
        .get("admin/all-settings")
        .then((res) => {
          this.settings = res.data.data || [];
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load settings" }))
        .finally(() => (this.loading = false));
    },
    openCreateModal() {
      this.isEditing = false;
      this.editId = null;
      this.form = { session_name: "", semester_name: null, session_id: null, semester_id: null };
      this.formErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editId = item.id;
      this.form = {
        session_name: item.session_name || "",
        semester_name: item.semester_name || null,
        session_id: item.session_id,
        semester_id: item.semester_id,
      };
      this.formErrors = {};
      this.showModal = true;
    },
    saveSetting() {
      this.formErrors = {};
      if (!this.form.session_name || !this.form.session_name.trim()) {
        this.formErrors.session_name = "Session name is required.";
        return;
      }
      if (!this.form.semester_name) {
        this.formErrors.semester_name = "Please select a semester.";
        return;
      }
      this.saving = true;
      let request;
      if (this.isEditing) {
        request = axios.put("admin/session-settings/" + this.editId, this.form);
      } else {
        request = axios.post("admin/session-settings", this.form);
      }
      request
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: this.isEditing ? "Setting updated" : "Setting created" });
            this.showModal = false;
            this.loadSettings();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Operation failed" });
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            const data = err.response.data;
            if (data.message) Toast.fire({ icon: "error", title: data.message });
            if (data.errors) {
              Object.keys(data.errors).forEach((k) => {
                this.formErrors[k] = data.errors[k][0];
              });
            }
          } else {
            Toast.fire({ icon: "error", title: "Failed to save setting" });
          }
        })
        .finally(() => (this.saving = false));
    },
    setActive(item) {
      this.activating = true;
      this.activatingId = item.id;
      axios
        .patch("admin/session-settings/" + item.id + "/set-active")
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: `${item.session_name} - ${item.semester_name} is now active` });
            this.loadSettings();
            this.$store.dispatch("getSettings");
          } else {
            Toast.fire({ icon: "error", title: "Failed to set active" });
          }
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to set active session" }))
        .finally(() => { this.activating = false; this.activatingId = null; });
    },
    confirmDelete(item) {
      if (item.status === "active") return;
      Swal.fire({
        title: "Delete this setting?",
        text: `${item.session_name} - ${item.semester_name} will be permanently removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete it",
      }).then((result) => {
        if (result.isConfirmed) this.deleteSetting(item);
      });
    },
    deleteSetting(item) {
      this.deletingId = item.id;
      axios
        .delete("admin/session-settings/" + item.id)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Setting deleted" });
            this.loadSettings();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Failed to delete" });
          }
        })
        .catch((err) => {
          const msg = err.response && err.response.data ? err.response.data.message : "Failed to delete setting";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.deletingId = null));
    },
    formatDate(val) {
      if (!val) return "N/A";
      return new Date(val).toLocaleDateString("en-NG", {
        year: "numeric", month: "short", day: "numeric",
      });
    },
  },
};
</script>
